import React, { useState } from 'react';
import { HtmlTable } from '../../../CommonComponents/OPL/HtmlTable/HtmlTable';
import { Text } from '../../../CommonComponents/OPL/Text/Text';
import { Tooltip } from '../../../CommonComponents/OPL/Tooltip/Tooltip';
import { EmptyState } from '../../../CommonComponents/OPL/AsyncStates/EmptyState/EmptyState';
import { Badge } from '../../../CommonComponents/OPL/Badge/Badge';
import { CampaignDTO } from '../../api/useCampaignsQuery';
import { Box } from '../../../CommonComponents/OPL/Box/Box';
import { IconButton } from '../../../CommonComponents/OPL/IconButton/IconButton';
import { toast } from '../../../CommonComponents/OPL/Toast/Toast';
import { CampaignDetails } from '../CampaignDetails/CampaignDetails';
import { TableRow } from '../../Campaigns.styles';
import { DeleteCampaignModal } from '../DeleteCampaignModal';

const COLUMNS_COUNT = 6;

interface Props {
  campaignsList?: CampaignDTO[];
  setCampaignToUpsert: (data: any) => void;
}

export const CampaignsList = ({ campaignsList, setCampaignToUpsert }: Props) => {
  const [campaignId, setCampaignId] = useState<string | null>(null);
  const [copiedToClipboard, setCopiedToClipboard] = useState<string | null>(null);
  const [campaignToDelete, setCampaignToDelete] = useState<CampaignDTO | null>(null);

  const copyToClipboard = async (id: string, link: string) => {
    try {
      await navigator.clipboard.writeText(link);

      setCopiedToClipboard(id);
      setTimeout(() => setCopiedToClipboard(null), 5000);
      toast.success('Campaign Link is copied successfully');
    } catch (error) {
      console.error(error);
      toast.error('Failed to copy the URL to the clipboard, try after some time');
    }
  };

  return (
    <>
      {campaignId && (
        <CampaignDetails campaignId={campaignId} onClose={() => setCampaignId(null)} />
      )}
      <HtmlTable minHeight={200} mb="24">
        <table>
          <thead>
            <tr>
              <th>
                <Text variant="smallCaps" textAlign="left">
                  Campaign
                </Text>
              </th>
              <th>
                <Text variant="smallCaps" textAlign="left">
                  Status
                </Text>
              </th>
              <th>
                <Text variant="smallCaps" textAlign="right">
                  Responses
                </Text>
              </th>
              <th>
                <Text variant="smallCaps" textAlign="right">
                  First Engagements
                </Text>
              </th>
              <th>
                <Tooltip
                  content="Number of users who want to speak with an advisor"
                  placement="top"
                >
                  <Text variant="smallCaps" textAlign="right">
                    Total Leads
                  </Text>
                </Tooltip>
              </th>
              <th aria-label="Actions for Campaigns"></th>
            </tr>
          </thead>
          <tbody>
            {campaignsList && campaignsList.length > 0 ? (
              campaignsList.map(campaign => (
                <TableRow key={campaign.id} onClick={() => setCampaignId(campaign.id)}>
                  <td style={{ maxWidth: 300 }}>
                    <Text isTruncated color={campaign.isActive ? 'neutral.700' : 'neutral.500'}>
                      {campaign.name}
                    </Text>
                  </td>
                  <td>
                    <Badge variant={campaign.isActive ? 'success' : 'tertiary'}>
                      {campaign.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                  </td>
                  <td>
                    <Text
                      variant="tabular"
                      textAlign="right"
                      color={campaign.isActive ? 'neutral.700' : 'neutral.500'}
                    >
                      {campaign.responses}
                    </Text>
                  </td>
                  <td>
                    <Text
                      variant="tabular"
                      textAlign="right"
                      color={campaign.isActive ? 'neutral.700' : 'neutral.500'}
                    >
                      {campaign.firstEngagements}
                    </Text>
                  </td>
                  <td>
                    <Text
                      variant="tabular"
                      textAlign="right"
                      color={campaign.isActive ? 'neutral.700' : 'neutral.500'}
                    >
                      {campaign.totalLeads}
                    </Text>
                  </td>
                  <Box as="td" display="flex" flexWrap="nowrap" css="text-align: right">
                    <Tooltip
                      content={copiedToClipboard === campaign.id ? 'Copied!' : 'Copy campaign link'}
                      placement="top"
                    >
                      <span>
                        <IconButton
                          icon="far fa-link"
                          variant="subtleInactive"
                          aria-label="Copy campaign link"
                          onClick={ev => {
                            ev.stopPropagation();
                            copyToClipboard(campaign.id, campaign.link);
                          }}
                        />
                      </span>
                    </Tooltip>

                    <Tooltip content="Edit campaign options" placement="top">
                      <span>
                        <IconButton
                          icon="fas fa-pencil-alt"
                          variant="subtleInactive"
                          aria-label="Edit campaign options"
                          onClick={ev => {
                            ev.stopPropagation();
                            setCampaignToUpsert(campaign);
                          }}
                        />
                      </span>
                    </Tooltip>

                    <Tooltip content="Delete campaign" placement="top">
                      <span>
                        <IconButton
                          icon="far fa-trash-alt"
                          variant="subtleInactive"
                          aria-label="Delete campaign"
                          onClick={ev => {
                            ev.stopPropagation();
                            setCampaignToDelete(campaign);
                          }}
                        />
                      </span>
                    </Tooltip>
                  </Box>
                </TableRow>
              ))
            ) : (
              <tr>
                <td colSpan={COLUMNS_COUNT}>
                  <EmptyState mx="auto" my="56" />
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </HtmlTable>
      {campaignToDelete && (
        <DeleteCampaignModal
          campaign={campaignToDelete}
          onClose={() => setCampaignToDelete(null)}
          onDelete={() => {
            setCampaignToDelete(null);
            setCampaignToUpsert(null);
          }}
        />
      )}
    </>
  );
};
